import { FC } from 'react';

type RaceBarProps = {
  /** Track percentages from mapRaceBarPositions (ghost < user < pace). */
  pacePercent: number;
  userPercent: number;
  ghostPercent: number;
  /** "You: P4 of 12" line, top-left. Omitted on the lateStart path. */
  positionLabel?: string;
  /** "🏁 Title pace" label, top-right. */
  titlePaceLabel: string;
  /** "YOU · 8 PTS BEHIND" pill above the track. */
  youPillText: string;
  /** "GW6 · 21 BEHIND" pill below the track. */
  ghostPillText: string;
  caption?: string;
};

const clampPillPercent = (value: number): number =>
  Math.max(12, Math.min(88, value));

/**
 * The verdict race bar (spec 6.5): amber user fill, dashed red GW6 ghost
 * marker, hatched red zone spanning ghost → title pace, green title flag
 * near the right edge, with labelled number pills above (YOU) and below
 * (GW6) the track.
 */
const RaceBar: FC<RaceBarProps> = ({
  pacePercent,
  userPercent,
  ghostPercent,
  positionLabel,
  titlePaceLabel,
  youPillText,
  ghostPillText,
  caption
}) => (
  <div>
    <div className="mb-2 flex items-center">
      {positionLabel && (
        <p className="font-heading text-sm font-bold text-primary-600">
          {positionLabel}
        </p>
      )}
      {/* ml-auto keeps the pace label right-aligned with or without
          the position label (omitted on the lateStart path) */}
      <p className="ml-auto font-heading text-sm font-bold text-greenfriday">
        {titlePaceLabel}
      </p>
    </div>

    {/* YOU pill above the track */}
    <div className="relative h-7">
      <span
        className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary-500 px-2.5 py-0.5 font-heading text-[11px] font-black tracking-wider text-white"
        style={{ left: `${clampPillPercent(userPercent)}%` }}
        data-cy="readiness-race-you-pill"
      >
        {youPillText}
      </span>
    </div>

    <div className="relative mt-1.5">
      <div className="h-3 overflow-hidden rounded-full bg-neutral-200">
        {/* Hatched at-risk zone: ghost → title pace */}
        <div
          className="absolute top-0 h-3"
          style={{
            left: `${ghostPercent}%`,
            width: `${Math.max(0, pacePercent - ghostPercent)}%`,
            background:
              'repeating-linear-gradient(135deg, #009DE5 0, #009DE5 4px, #75CAF1 4px, #75CAF1 8px)'
          }}
        />
        {/* Primary-blue fill to the user's position */}
        <div
          className="absolute top-0 h-3 rounded-l-full bg-primary-500"
          style={{ width: `${userPercent}%` }}
        />
      </div>
      {/* Dashed ghost marker */}
      <span
        className="absolute -top-1 h-5 w-0 -translate-x-1/2 border-l-2 border-dashed border-neutral-400"
        style={{ left: `${ghostPercent}%` }}
      />
      {/* Title pace flag */}
      <span
        className="absolute -top-1 h-5 w-0.5 -translate-x-1/2 rounded bg-greenfriday"
        style={{ left: `${pacePercent}%` }}
      />
    </div>

    {/* Ghost pill below the track */}
    <div className="relative mt-2 h-6">
      <span
        className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-full border border-dashed border-neutral-400 px-2.5 py-0.5 font-heading text-[11px] font-bold tracking-wider text-neutral-600"
        style={{ left: `${clampPillPercent(ghostPercent)}%` }}
        data-cy="readiness-race-ghost-pill"
      >
        {ghostPillText}
      </span>
    </div>

    {caption && (
      <p className="mt-3 font-heading text-sm font-bold text-danger-600">
        {caption}
      </p>
    )}
  </div>
);

export default RaceBar;
